import _ from 'lodash'
import { decorator as reusePromise } from 'reuse-promise'
import * as errors from '../errors'
import { GroupType, SearchType } from '../models/enums'
import SearchResult from '../models/SearchResult'
import { PROFILE_ATTRIBUTES_PER_ORG } from '../models/User'
import { Camelizer, arrayWrap, replaceKey } from '../utils'
import { isEmail } from '../utils/email'
import { formatPhone, isPhone, normalizePhone } from '../utils/phone'
import BaseService from './BaseService'

const DEFAULT_RETURN_FIELDS = [
  'avatar',
  'conversation_id',
  'department',
  'description',
  'display_name',
  'dnd_auto_forward_receiver',
  'dnd',
  'first_name',
  'is_public',
  'last_login_time',
  'last_name',
  'name',
  'num_members',
  'organization_key',
  'pagers',
  'status',
  'title',
  'token',
]

const MAP_SEARCH_TYPE_TO_SERVER_TYPE = {
  'distributionList': SearchType.toServer(SearchType.DISTRIBUTION_LIST),
  'forum': SearchType.toServer(SearchType.FORUM),
  'group': SearchType.toServer(SearchType.GROUP),
  'organization': SearchType.toServer(SearchType.ORGANIZATION),
  'patient_account': SearchType.toServer(SearchType.PATIENT),
  'tag': SearchType.toServer(SearchType.TAG),
  'user': SearchType.toServer(SearchType.USER),
}

const VALID_SERVER_SEARCH_TYPES = Object.values(MAP_SEARCH_TYPE_TO_SERVER_TYPE)

export default class SearchService extends BaseService {
  @reusePromise()
  async query(options) {
    let {
      continuation = null,
      excludeIds = [],
      excludeSelf = false,
      followContinuations = false,
      includeDisabled = false,
      organizationId = null,
      organizationIds = [],
      query = {},
      resultsFormat = 'entities',
      returnFields = DEFAULT_RETURN_FIELDS,
      sort = [],
      types = [],
    } = options

    if (types.length === 0) {
      throw new errors.ValidationError('types', 'required', 'no types specified')
    }

    types = _.uniq(types.map(type => (
      SearchType.isValid(type) ? SearchType.toServer(type) : MAP_SEARCH_TYPE_TO_SERVER_TYPE[type]
    )))
    const invalidTypes = _.difference(types, VALID_SERVER_SEARCH_TYPES)

    if (invalidTypes.length > 0) {
      throw new errors.ValidationError('types', 'invalid', `invalid types: ${invalidTypes.join(', ')}`)
    }

    if (organizationId) organizationIds = [organizationId]
    if (organizationIds.length === 0) {
      throw new errors.ValidationError(
        'organizationIds', 'invalid', 'search must be done in a context of an organization'
      )
    }

    if (organizationIds.length === 1) {
      const organization = this.host.models.Organization.get(organizationId)
      if (organization && organization.isContacts) {
        const { results, metadata } = this._queryLocalOrganization({ organization, query, types })
        return { results, metadata }
      }
    }

    if (resultsFormat === 'ids') resultsFormat = 'tokens'
    excludeIds = arrayWrap(excludeIds)

    const params = {
      'directory': organizationIds,
      'include_disabled': includeDisabled,
      'render_metadata': true,
      'results_format': resultsFormat,
      'return_fields': returnFields,
      'type': types,
    }

    if (sort && sort.length) {
      params['sort'] = sort.map(entry => Camelizer.underscoreKey(entry))
    }

    // exclude_tokens currently not working: https://tigertext.atlassian.net/browse/TS-1946
    // if (excludeIds.length) params['exclude_tokens'] = excludeIds

    let serverQuery = {}
    if (query && Object.keys(query).length > 0) {
      for (const [key, value] of Object.entries(query)) {
        if (key.includes(',')) {
          const serverKeys = key.split(',').map(Camelizer.underscoreKey).join(',')
          serverQuery[serverKeys] = value

          delete query[key]
        }
      }

      serverQuery = { ...serverQuery, ...Camelizer.underscoreObject(query) }
      params['bool'] = { 'must': serverQuery }
    }

    const baseQueryOptions = {
      continuation,
      excludeIds,
      excludeSelf,
      followContinuations,
      params,
    }
    const searchQueryOptions = [baseQueryOptions]

    const allResults = await Promise.all(searchQueryOptions.map(this._query))
    let toReturn = []

    for (const { results } of allResults) {
      for (const singleResult of results) {
        toReturn.push(singleResult)
      }
    }

    toReturn = _.uniqBy(toReturn, 'entity')

    return { results: toReturn, metadata: allResults[0].metadata }
  }

  _query = async (options) => {
    let {
      continuation = null,
      excludeIds = [],
      excludeSelf = false,
      followContinuations = false,
      params,
    } = options

    if (continuation) {
      params = { ...params, continuation }
    }

    let res
    let entries = []

    if (params.type.length > 1 && params.type.includes('patient_account')) {
      throw new errors.ValidationError('types', 'required', `invalid types: ${params.type.join(', ')}`)
    }

    if (params.type.includes('patient_account')) {
      res = await this.host.api.search.patientQuery(params)
      if (!res.metadata.continuation) {
        res.metadata.continuation = null
      }
    } else {
      res = await this.host.api.search.query(params)
    }

    entries = res.results

    const candidates = []

    for (const entry of entries) {
      const type = entry['type']
      const entityAttrs = entry['entity']
      const organizationId = entry['organization_id']
      const candidate = {
        entityAttrs,
        organizationId,
        type,
      }

      if (this.config.condensedReplays) {
        if (entityAttrs.conversation_id) {
          candidate.conversationId = entityAttrs.conversation_id
          delete entityAttrs.conversation_id
        } else {
          candidate.conversationId = entry['conversation_id']
        }
      } else {
        delete entityAttrs.conversation_id
      }

      if (Object.keys(entityAttrs).length === 0) {
        continue
      }

      // normalize fields that are different on search compared to most other endpoints
      if (type === 'tigertext:entity:account') {
        replaceKey(entityAttrs, 'status', 'account_status')
      } else if (type === 'tigertext:entity:tag') {
        entityAttrs.token = `${organizationId}:${entityAttrs.token}`
      }

      const metadata = entityAttrs.metadata
      if (metadata) {
        this.host.metadata.__injectMetadata(entityAttrs.token, organizationId, metadata)
      }

      candidates.push(candidate)
    }

    const {
      'continuation': nextContinuation,
      'first_hit': firstHit,
      'total_hits': totalHits,
    } = res.metadata
    let searchMetadata = { firstHit, totalHits }

    if (nextContinuation) {
      if (followContinuations) {
        searchMetadata.firstHit = 1
      } else {
        searchMetadata.continuation = nextContinuation
      }
    }

    const toReturn = []
    const extraPromises = []
    const recordResults = ({ results }) => {
      for (const result of results) {
        toReturn.push(result)
      }
    }

    if (followContinuations && nextContinuation) {
      extraPromises.push(
        this._query({ ...options, continuation: nextContinuation }).then(recordResults)
      )
    }

    await Promise.all(extraPromises)

    const immediateResults = []
    for (const entry of candidates) {
      const { conversationId, entityAttrs, organizationId, type } = entry
      const entityType = this.host.modelNameByTypeNS(type)
      const groupType = entityType === 'group' && this.host.groups.__extractGroupTypeFromAttrs(entityAttrs)

      if (groupType === GroupType.FORUM) {
        delete entityAttrs.members
      }

      const metadataEntity = this.host.metadata.get(entityAttrs.token, organizationId)
      const metadata = metadataEntity ? metadataEntity.data : null
      let entity = this.host.conversations.__injectCounterParty({
        conversationId, entityAttrs, entityType, groupType, hasCurrentUser: false, organizationId,
      })
      if (entity.$entityType === 'user' && metadata && metadata['feature_service'] === 'role') {
        this.host.roles.__injectSearchResult({ entity, metadata, organizationId })
      }

      if (excludeIds.length && excludeIds.includes(entity.id)) {
        continue
      }

      // TODO test
      const currentUserId = this.host.currentUserId
      if (excludeSelf && entity.id === currentUserId) {
        continue
      }

      if (!entity) {
        this.logger.warn('no entity for ', entry)
        continue
      }

      if (entity.isRoleBot && entity.botRole) entity = entity.botRole
      const searchResult = new SearchResult(entity, organizationId, conversationId, metadata)
      immediateResults.unshift(searchResult)
    }

    for (const result of immediateResults) toReturn.unshift(result)

    return { results: toReturn, metadata: searchMetadata }
  }

  _queryLocalOrganization({ organization, query, types }) {
    const organizationId = organization.id
    const results = this._searchLocalOrganization({ organization, query, types })
    const metadata = { firstHit: 1, totalHits: results.length }
    const fields = Object.keys(query)

    if (results.length === 0 && fields.length > 0) {
      const displayNameField = fields.find(field => field.includes('displayName'))
      const input = displayNameField ? query[displayNameField] : query[fields[0]]

      if (isEmail(input) || isPhone(input)) {
        const recipient = isEmail(input) ? input : normalizePhone(input)

        const user = this.host.models.User.createInstance({
          displayName: isPhone(input) ? formatPhone(input) : input,
          id: recipient,
          messageAnyoneRecipient: true,
          organizationId,
        })

        const searchResult = new SearchResult(user, organizationId, metadata)

        return { results: [searchResult], metadata }
      }
    }

    return { results, metadata }
  }

  _searchLocalOrganization = ({ organization, query, types = [] }) => {
    const { conversations, id: organizationId } = organization
    const results = []

    for (const { counterParty, counterPartyType, metadata } of conversations) {
      const searchType = MAP_SEARCH_TYPE_TO_SERVER_TYPE[counterPartyType]
      if (!types.includes(searchType)) continue

      if (!query || Object.keys(query).length === 0) {
        results.push(new SearchResult(counterParty, organizationId, metadata))
        continue
      }

      let andMatch = true
      for (let [fields, term] of Object.entries(query)) {
        fields = fields.split(',')
        term = term.toLowerCase()
        let orMatch = false

        for (const field of fields) {
          const searchArea = counterPartyType === 'user' && PROFILE_ATTRIBUTES_PER_ORG.includes(field) ?
            counterParty.profileByOrganizationId[organizationId] :
            counterParty

          if (!searchArea) continue

          const fieldValue = searchArea[field]
          if (fieldValue !== undefined && fieldValue.toLowerCase().includes(term)) {
            orMatch = true
            break
          }
        }

        if (!orMatch) {
          andMatch = false
          break
        }
      }

      if (andMatch) {
        results.push(new SearchResult(counterParty, organizationId, metadata))
      }
    }

    return results
  }
}
